import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { api } from '@/api/Api';
import { CashAuditModel, CashAuditStatus } from '@/api/AuditController';
import { hasAnyRole, hasDepartmentScope } from '@/core/helpers/PageHelpers';
import { RolesData } from '@/interface';
import { StateReducer } from '@/redux/modules/reducer';
import { selectAllInitialDepartments } from '@/redux/modules/department/departmentSelectors';

import { auditDepartmentLabel } from './auditHelpers';

/**
 * Ревизии на сегодня — для баннера в шапке.
 *
 * Опрашиваем GET /CashAudit/planned раз в пять минут: ревизию назначает планировщик на бэке,
 * и без опроса дежурный узнал бы о ней только зайдя в раздел.
 *
 * Кому что видно, решает список доступных отделений: селектор уже прогнал его через
 * filterDepartmentsByRole, то есть главный кассир и менеджер получают отделения своего города,
 * директор франшизы — отделения своего тенанта, а директор без тенанта — все.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const AUDIT_BANNER_ROLES: (keyof RolesData)[] = ["isAdmin", "isDirector", "mainCashierCity", "managerCity"];

/**
 * Отменённые и завершённые напоминать незачем, всё остальное на сегодня — повод для баннера.
 */
const isPending = (audit: CashAuditModel) => (
    audit?.status !== CashAuditStatus.Completed
    && audit?.status !== CashAuditStatus.ActCreated
    && audit?.status !== CashAuditStatus.Rejected
);

/**
 * Дату сравниваем календарно, а не через парсинг момента: бэк отдаёт день ревизии
 * UTC-полуночью («2026-09-14T00:00:00Z»), и в зоне с отрицательным смещением
 * dayjs(plannedDate) уехал бы на предыдущий день.
 */
const isToday = (audit: CashAuditModel) => (
    !!audit?.plannedDate && audit.plannedDate.slice(0, 10) === dayjs().format("YYYY-MM-DD")
);

/**
 * Результат последнего опроса. Header пересоздаётся на каждом переходе между страницами,
 * и без кэша бэк опрашивался бы не раз в пять минут, а на каждую навигацию.
 */
let cachedAudits: CashAuditModel[] = [];
let cachedOwner = "";
let lastFetchAt = 0;

export const useTodayAudits = (): CashAuditModel[] => {
    const rolesData = useSelector((state: StateReducer) => state?.header?.rolesData);
    const departmentList = useSelector(selectAllInitialDepartments);
    const userLogin = useSelector((state: StateReducer) => state?.header?.userContext?.user?.login || "");

    const [todayAudits, setTodayAudits] = useState<CashAuditModel[]>([]);

    useEffect(() => {
        if (!hasAnyRole(rolesData, AUDIT_BANNER_ROLES)) {
            setTodayAudits([]);
            return;
        }

        // Fail-closed: ролям с ограничением по городу и тенанту без списка отделений
        // нечем отсеять чужие точки — ждём, пока он загрузится
        const isRestricted = hasDepartmentScope(rolesData);

        if (isRestricted && !departmentList?.length) {
            setTodayAudits([]);
            return;
        }

        const allowedDepIds = new Set((departmentList || []).map((dep) => dep?.id));
        let cancelled = false;

        const checkTodayAudits = async (force: boolean) => {
            const isCacheFresh = cachedOwner === userLogin && Date.now() - lastFetchAt < POLL_INTERVAL_MS;

            if (!force && isCacheFresh) {
                setTodayAudits(cachedAudits);
                return;
            }

            try {
                const res = await api.audit.getPlannedAudits();
                const audits = (res?.items || [])
                    .filter((audit) => (isRestricted ? allowedDepIds.has(audit?.depId) : true))
                    .filter(isToday)
                    .filter(isPending);

                cachedAudits = audits;
                cachedOwner = userLogin;
                lastFetchAt = Date.now();

                if (!cancelled) {
                    setTodayAudits(audits);
                }
            } catch (error) {
                // Баннер не критичен: молча оставляем прежнее состояние до следующего опроса
                console.error("Не удалось получить ревизии на сегодня", error);
            }
        };

        checkTodayAudits(false);
        const intervalId = setInterval(() => checkTodayAudits(true), POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [rolesData, departmentList, userLogin]);

    return todayAudits;
};

/** Текст для бегущей строки: отделения перечисляем, длинный список подрезаем */
export const todayAuditsText = (audits: CashAuditModel[], departments: { id: number; code?: string }[]) => {
    if (!audits.length) {
        return "";
    }

    const deps = Array.from(new Set(audits.map((audit) => auditDepartmentLabel(audit, departments))));
    const shown = deps.slice(0, 3).join(", ");
    const rest = deps.length - 3;

    return `Сегодня нужно провести ревизию: ${shown}${rest > 0 ? ` и ещё ${rest}` : ""}`;
};
